import type { Event } from 'react-big-calendar';
import type { ValueOf } from 'type-fest';
import {
  RecurringFrequency,
  TourConfirmationType,
  TourDuration,
  type TourWebApiDto,
} from '@zg-rentals/ts-z-calendar-client';

export const Z_CALENDAR_EVENT_TYPE = {
  TOUR: 'TOUR',
  AVAILABILITY: 'AVAILABILITY',
  IN_PROGRESS_AVAILABILITY: 'IN_PROGRESS_AVAILABILITY',
} as const;

export type Z_CALENDAR_EVENT_TYPE = ValueOf<typeof Z_CALENDAR_EVENT_TYPE>;

// Timeslot-group min-heights (px). A react-big-calendar day-slot event is sized proportionally to
// the group, so these drive how tall a tour tile renders. Single source of truth shared by
// calendarStyles (the CSS min-height) and the tile-height calculation in calendarUtils.
export const TIMESLOT_GROUP_MIN_HEIGHT_PX = 100;
export const TIMESLOT_GROUP_MIN_HEIGHT_MINUTES_20_PX = 150;

// Mirrors Panda's `breakpoints.lg`. Inlined to avoid pulling `token()` into the bundle.
export const LG_BREAKPOINT = '48em';

// Mirrors Panda's `breakpoints.md`. Inlined to avoid pulling `token()` into the bundle.
export const MD_BREAKPOINT = '30em';

// How far ahead a landlord can open tour availability — bounds the calendar's fetch ranges, week
// navigation, date pickers and recurrence end dates. Independent of the Upcoming Tours tile window.
export const AVAILABILITY_LOOKAHEAD_WEEKS = 3;

export interface BaseZCalendarEvent extends Event {
  id: string;
  eventType: Z_CALENDAR_EVENT_TYPE;
  isPastEvent: boolean;
}

export interface AvailabilityEvent extends BaseZCalendarEvent {
  id: string;
  start: Date;
  end: Date;
  isRecurringEvent: boolean;
  frequency?: RecurringFrequency;
  recurringDays?: AvailabilityEventWeeklyRecurringDaysMap;
  recurrenceEndDateMs?: number;
}

export interface BaseTourEvent extends BaseZCalendarEvent {
  start: Date;
  end: Date;
}
export type TourEvent = BaseTourEvent & Omit<TourWebApiDto, 'startMs' | 'endMs' | 'startDateTime' | 'endDateTime'>;

export type ZCalendarEvent = AvailabilityEvent | TourEvent;

export const RecurringFrequencyLabels = {
  NEVER: 'Never',
  DAILY: 'Every day',
  WEEKLY: 'Every week',
} as const;
export type RecurringFrequencyLabels = ValueOf<typeof RecurringFrequencyLabels>;

export const RecurringFrequencyNeverValue = 'NEVER';
export type RecurringFrequencyValues = RecurringFrequency | typeof RecurringFrequencyNeverValue;

export const RecurringFrequencyValueLabels = [
  {
    label: RecurringFrequencyLabels.NEVER,
    value: RecurringFrequencyNeverValue,
    'data-testid': 'AvailabilityEventEditPopoverBody-RepeatDropdownOption-Never',
  },
  {
    label: RecurringFrequencyLabels.DAILY,
    value: RecurringFrequency.DAILY,
    'data-testid': 'AvailabilityEventEditPopoverBody-RepeatDropdownOption-Daily',
  },
  {
    label: RecurringFrequencyLabels.WEEKLY,
    value: RecurringFrequency.WEEKLY,
    'data-testid': 'AvailabilityEventEditPopoverBody-RepeatDropdownOption-Weekly',
  },
];
export const RecurringFrequencyLabelToValueMap = {
  [RecurringFrequencyLabels.NEVER]: RecurringFrequencyNeverValue,
  [RecurringFrequencyLabels.DAILY]: RecurringFrequency.DAILY,
  [RecurringFrequencyLabels.WEEKLY]: RecurringFrequency.WEEKLY,
} as const;

export const WeeklyRecurringDays = {
  SUNDAY: 'SUNDAY',
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
} as const;

export type WeeklyRecurringDays = ValueOf<typeof WeeklyRecurringDays>;

export const WeekdayToNumber: { [key in WeeklyRecurringDays]: number } = {
  [WeeklyRecurringDays.SUNDAY]: 0,
  [WeeklyRecurringDays.MONDAY]: 1,
  [WeeklyRecurringDays.TUESDAY]: 2,
  [WeeklyRecurringDays.WEDNESDAY]: 3,
  [WeeklyRecurringDays.THURSDAY]: 4,
  [WeeklyRecurringDays.FRIDAY]: 5,
  [WeeklyRecurringDays.SATURDAY]: 6,
} as const;

export const RecurringWeeklyButtonsValueLabels = [
  { label: 'S', value: WeeklyRecurringDays.SUNDAY },
  { label: 'M', value: WeeklyRecurringDays.MONDAY },
  { label: 'T', value: WeeklyRecurringDays.TUESDAY },
  { label: 'W', value: WeeklyRecurringDays.WEDNESDAY },
  { label: 'T', value: WeeklyRecurringDays.THURSDAY },
  { label: 'F', value: WeeklyRecurringDays.FRIDAY },
  { label: 'S', value: WeeklyRecurringDays.SATURDAY },
] as const;

export const DefaultSelectedWeeklyRecurringDays = {
  [WeeklyRecurringDays.SUNDAY]: false,
  [WeeklyRecurringDays.MONDAY]: false,
  [WeeklyRecurringDays.TUESDAY]: false,
  [WeeklyRecurringDays.WEDNESDAY]: false,
  [WeeklyRecurringDays.THURSDAY]: false,
  [WeeklyRecurringDays.FRIDAY]: false,
  [WeeklyRecurringDays.SATURDAY]: false,
};
export type AvailabilityEventWeeklyRecurringDaysMap = typeof DefaultSelectedWeeklyRecurringDays;

export const TimeZoneIds = {
  ET: 'America/New_York',
  CT: 'America/Chicago',
  CT_REGINA: 'America/Regina',
  MT: 'America/Denver',
  MT_AZ: 'America/Phoenix',
  PT: 'America/Los_Angeles',
  AKT: 'America/Anchorage',
  HT: 'Pacific/Honolulu',
} as const;

export type TimeZoneIds = ValueOf<typeof TimeZoneIds>;

export const AbbreviatedTimeZones = {
  [TimeZoneIds.ET]: 'ET',
  [TimeZoneIds.CT]: 'CT',
  [TimeZoneIds.CT_REGINA]: 'CT',
  [TimeZoneIds.MT]: 'MT',
  [TimeZoneIds.MT_AZ]: 'MT',
  [TimeZoneIds.PT]: 'PT',
  [TimeZoneIds.AKT]: 'AKT',
  [TimeZoneIds.HT]: 'HT',
} as const;

export const DisplayTimeZones = {
  [TimeZoneIds.ET]: 'Eastern time (ET)',
  [TimeZoneIds.CT]: 'Central time (CT)',
  [TimeZoneIds.CT_REGINA]: 'Central time (CT) - Saskatchewan',
  [TimeZoneIds.MT]: 'Mountain time (MT)',
  [TimeZoneIds.MT_AZ]: 'Mountain time (MT) - Arizona',
  [TimeZoneIds.PT]: 'Pacific time (PT)',
  [TimeZoneIds.AKT]: 'Alaska time (AKT)',
  [TimeZoneIds.HT]: 'Hawaii time (HT)',
} as const;

export const TimeZoneDropdownOptions = [
  { label: 'Eastern time (ET)', value: TimeZoneIds.ET },
  { label: 'Central time (CT)', value: TimeZoneIds.CT },
  { label: 'Central time (CT) - Saskatchewan', value: TimeZoneIds.CT_REGINA },
  { label: 'Mountain time (MT)', value: TimeZoneIds.MT },
  { label: 'Mountain time (MT) - Arizona', value: TimeZoneIds.MT_AZ },
  { label: 'Mountain time (MT) - Navajo County', value: TimeZoneIds.MT },
  { label: 'Pacific time (PT)', value: TimeZoneIds.PT },
  { label: 'Alaska time (AKT)', value: TimeZoneIds.AKT },
  { label: 'Hawaii time (HT)', value: TimeZoneIds.HT },
];

export const TOUR_CALENDAR_HEADER = 'When are you free to give tours?';
export const getTourCalendarDescription = (isInstandBookSelected: boolean) =>
  `Add your availability to tell renters when you’re free to show the property. You’ll get notified anytime someone ${isInstandBookSelected ? 'books' : 'requests'} a tour.`;

export const TOUR_CALENDAR_MODES = {
  TOUR_AVAILABILITY: 'TOUR_AVAILABILITY',
  FULL_CALENDAR: 'FULL_CALENDAR',
} as const;

export type TOUR_CALENDAR_MODES = ValueOf<typeof TOUR_CALENDAR_MODES>;

export const TourWizardSteps = {
  CONFIG: 'config',
  TIME_ZONE: 'timeZone',
  CALENDAR: 'calendar',
} as const;

export type TourWizardSteps = ValueOf<typeof TourWizardSteps>;

export type TOUR_OPTION = {
  id: TourConfirmationType;
  title: string;
  subTitle: string;
  description: string;
};

export const DisplayTourTypes = {
  [TourConfirmationType.INSTANT]: 'Book instantly',
  [TourConfirmationType.LANDLORD_CONFIRM]: 'Review & confirm',
};

export const TOUR_OPTIONS: Array<TOUR_OPTION> = [
  {
    id: TourConfirmationType.INSTANT,
    title: DisplayTourTypes[TourConfirmationType.INSTANT],
    subTitle: 'MOST EFFICIENT',
    description: 'You provide your availability so renters can book a tour instantly from your listing.',
  },
  {
    id: TourConfirmationType.LANDLORD_CONFIRM,
    title: DisplayTourTypes[TourConfirmationType.LANDLORD_CONFIRM],
    subTitle: 'MORE CONTROL',
    description: 'You provide your availability on your listing, review tour requests, then confirm or decline them.',
  },
];

export type OnCalendarEventSaveClickParams = {
  selectedEventDate: string;
  selectedStartTime: string;
  selectedEndTime: string;
  selectedFrequency?: RecurringFrequency;
  selectedWeeklyRecurringDays: AvailabilityEventWeeklyRecurringDaysMap;
  selectedRepeatUntilDate: string;
};

export type CalendarEventHandlers = {
  onClose: () => void;
  onEditClick: () => void;
  onDeleteClick: (removeOnlyThisEvent: boolean) => void;
  onSaveClick: (params: OnCalendarEventSaveClickParams) => void;
  onDeclineClick: () => void;
  onConfirmClick: () => void;
  onCancelTourConfirmationClick: () => void;
  onRescheduleSuccess: () => void;
};

export const TOUR_CONFIRMATION_TYPE_LAYOUT = {
  VERTICAL: 'VERTICAL',
  HORIZONTAL: 'HORIZONTAL',
} as const;

export type TOUR_CONFIRMATION_TYPE_LAYOUT = ValueOf<typeof TOUR_CONFIRMATION_TYPE_LAYOUT>;

export const CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING = 'EEEE, MMMM d';
export const CALENDAR_AVAILABILITY_INPUT_FORMAT_STRING = 'MM/dd/yyyy';
export const TWENTY_FOUR_HOUR_FORMAT_STRING = 'HH:mm';
export const HOUR_FORMAT_STRING = 'h';
export const HOUR_MERIDIEM_FORMAT_STRING = 'ha';
export const HOUR_MINUTE_FORMAT_STRING = 'h:mm';
export const HOUR_MINUTE_MERIDIEM_FORMAT_STRING = 'h:mma';
export const DEFAULT_CALENDAR_START_TIME = { hour: 7, minute: 0, second: 0, nano: 0 };
export const DEFAULT_CALENDAR_END_TIME = { hour: 21, minute: 0, second: 0, nano: 0 };

export const TOUR_DURATION_DROPDOWN_OPTIONS = [
  { label: '15 min', value: TourDuration.MINUTES_15 },
  { label: '20 min', value: TourDuration.MINUTES_20 },
  { label: '30 min', value: TourDuration.MINUTES_30 },
  { label: '60 min', value: TourDuration.MINUTES_60 },
] as const;

export const TOUR_DURATION_NUMBER_MAP = {
  [TourDuration.MINUTES_15]: 15,
  [TourDuration.MINUTES_20]: 20,
  [TourDuration.MINUTES_30]: 30,
  [TourDuration.MINUTES_60]: 60,
} as const;

export const NUMBER_TO_TOUR_DURATION_MAP = {
  15: TourDuration.MINUTES_15,
  20: TourDuration.MINUTES_20,
  30: TourDuration.MINUTES_30,
  60: TourDuration.MINUTES_60,
} as const;

export const TOUR_DURATION_DISPLAY_MAP = {
  [TourDuration.MINUTES_15]: '15 minutes',
  [TourDuration.MINUTES_20]: '20 minutes',
  [TourDuration.MINUTES_30]: '30 minutes',
  [TourDuration.MINUTES_60]: '60 minutes',
} as const;
